export const SITE_ORIGIN = "https://shariqrefai.com";
export const SITE_NAME = "Shariq Refai, MD, MBA";
export const DEFAULT_IMAGE = `${SITE_ORIGIN}/opengraph.jpg`;
export const PERSON_ID = `${SITE_ORIGIN}/#person`;
export const ORG_ID = `${SITE_ORIGIN}/#website`;
export const PUBLISHER_ID = `${SITE_ORIGIN}/#publisher`;
export const CLINICAL_ORG_URL = "https://shrinkmd.com/#organization";

export function abs(path: string): string {
  if (!path) return SITE_ORIGIN;
  if (path.startsWith("http")) return path;
  return `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

export const EDITORIAL_PROCESS_URL = `${SITE_ORIGIN}/editorial-process`;

export const ORCID_ID = "0009-0009-1090-4373";
export const ORCID_URL = `https://orcid.org/${ORCID_ID}`;
export const WIKIDATA_ID = "Q139822307";
export const WIKIDATA_URL = `https://www.wikidata.org/wiki/${WIKIDATA_ID}`;
export const ANXIETY_RESOURCE_REVIEWER_URL = "https://anxietyresource.org/contributors/shariq-refai/";
export const DEPRESSION_RESOURCE_REVIEWER_URL = "https://depressionresource.org/about/shariq-refai/";
export const ANXIETY_RESEARCH_REVIEWER_URL = "https://anxietyresearch.org/medical-review-board/";
export const PSYCHIATRY_RX_REVIEWER_URL = "https://psychiatryrx.org/medical-editor/";
export const NPI = "1467680660";
export const NPI_REGISTRY_URL = `https://npiregistry.cms.hhs.gov/provider-view/${NPI}`;
export const SHRINK_NETWORK_URL = "https://shrinknetwork.com";
export const SHRINK_NETWORK_ORG_URL = "https://shrinknetwork.com/#organization";
export const SHRINK_NETWORK_WIKIDATA = "https://www.wikidata.org/wiki/Q139972275";
export const SHRINKMD_WIKIDATA = "https://www.wikidata.org/wiki/Q139976539";
export const SHRINQ_WIKIDATA = "https://www.wikidata.org/wiki/Q139984236";
export const SHRINK_NETWORK_NAME = "Shrink Network";

export interface BookMeta {
  title: string;
  slug: string;
  isbn13: string;
  isbn10: string;
  amazonUrl: string;
  bnUrl: string;
  appleBooksUrl: string;
  bookshopUrl: string;
  samplePdfUrl: string;
  coverImageUrl: string;
  publicationYear: string;
  publicationDate: string;
  format: "Hardcover" | "Paperback" | "EBook" | "AudiobookFormat";
  pageCount: number;
}

// Book metadata. Empty strings/zeros mean "not yet available";
// rendering on book pages must be conditional on these fields.
export const BOOKS: Record<"ymifos" | "workbook" | "havoc", BookMeta> = {
  ymifos: {
    title: "Your Mind Is Full of Sh*t",
    slug: "your-mind-is-full-of-shit",
    isbn13: "979-8-950653-00-1",
    isbn10: "",
    amazonUrl: "",
    bnUrl: "",
    appleBooksUrl: "",
    bookshopUrl: "",
    samplePdfUrl: "",
    coverImageUrl: `${SITE_ORIGIN}/books/your-mind-is-full-of-shit/cover.webp`,
    publicationYear: "2026",
    publicationDate: "",
    format: "Paperback",
    pageCount: 0,
  },
  workbook: {
    title: "Your Mind Is Full of Sh*t: The Workbook",
    slug: "your-mind-is-full-of-shit-workbook",
    isbn13: "",
    isbn10: "",
    amazonUrl: "",
    bnUrl: "",
    appleBooksUrl: "",
    bookshopUrl: "",
    samplePdfUrl: "",
    coverImageUrl: `${SITE_ORIGIN}/books/your-mind-is-full-of-shit-workbook/cover.webp`,
    publicationYear: "2026",
    publicationDate: "",
    format: "Paperback",
    pageCount: 0,
  },
  havoc: {
    title: "The Havoc in Your Head",
    slug: "the-havoc-in-your-head",
    isbn13: "",
    isbn10: "",
    amazonUrl: "",
    bnUrl: "",
    appleBooksUrl: "",
    bookshopUrl: "",
    samplePdfUrl: "",
    coverImageUrl: `${SITE_ORIGIN}/books/the-havoc-in-your-head/cover.webp`,
    publicationYear: "2026",
    publicationDate: "",
    format: "Hardcover",
    pageCount: 0,
  },
};

export interface PressFeature {
  publisher: string;
  publisherUrl: string;
  headline: string;
  url: string;
  topic: string;
  datePublished: string;
}

// Press features where Dr. Refai has appeared as an expert source, ordered
// newest first. Single source of truth: the Media page, About page, and
// Homepage press blocks all render from this array. datePublished is left
// empty when it can't be verified from the live article.
export const PRESS_FEATURES: PressFeature[] = [
  {
    publisher: "The Epoch Times",
    publisherUrl: "https://www.theepochtimes.com",
    headline: "10 Research-Backed, Natural Ways to Counter Depression",
    url: "https://www.theepochtimes.com/health/research-backed-natural-ways-to-treat-depression-5873396",
    topic: "Depression and natural approaches to mental health",
    datePublished: "2025-07-04",
  },
  {
    publisher: "EatingWell",
    publisherUrl: "https://www.eatingwell.com",
    headline: "Do You Poop When You're Stressed? Experts Explain Why",
    url: "https://www.eatingwell.com/why-does-stress-make-you-poop-11776377",
    topic: "Stress, the nervous system, and the gut-brain connection",
    datePublished: "",
  },
  {
    publisher: "DomesticShelters.org",
    publisherUrl: "https://www.domesticshelters.org",
    headline:
      "Survivors in Isolation: Abusers Separate Victims from Everyone for Power and Control",
    url: "https://www.domesticshelters.org/articles/identifying-abuse/survivors-in-isolation-abusers-separate-victims-from-everyone-for-power-and-control",
    topic: "Coercive control, isolation, and the psychology of abuse",
    datePublished: "2025-08-11",
  },
  {
    publisher: "Top Doctor Magazine",
    publisherUrl: "https://topdoctormagazine.com",
    headline: "Mental Health Does Not Discriminate: An Interview With Dr. Shariq Refai",
    url: "https://topdoctormagazine.com/mental-health/mental-health-does-not-discriminate-an-interview-with-dr-shariq-refai/",
    topic: "Interview feature on psychiatry and access",
    datePublished: "",
  },
  {
    publisher: "Duval County Medical Society",
    publisherUrl: "https://www.dcmsonline.org",
    headline: "Full Circle Calling: Spotlight on Shariq Refai, MD, MBA, FAPA",
    url: "https://www.dcmsonline.org/news/700524/Full-Circle-Calling-Spotlight-on-DCMS-Member-Shariq-Refai-MD-MBA-FAPA.htm",
    topic: "Member spotlight on training, practice, and career",
    datePublished: "",
  },
  {
    publisher: "SoMeDocs, Doctors on Social Media",
    publisherUrl: "https://doctorsonsocialmedia.com",
    headline: "Shrink Wrapped with Dr. Refai, video series",
    url: "https://doctorsonsocialmedia.com/shrink-wrapped-with-dr-refai/",
    topic: "Ongoing video series on anxiety, bipolar disorder, athlete mental health, and modern psychiatry",
    datePublished: "",
  },
  {
    publisher: "MedStory Podcast",
    publisherUrl: "https://podcasts.apple.com/us/podcast/medstory-podcast/id1804185340",
    headline: "The Mental Health Emergency No One Can Escape",
    url: "https://podcasts.apple.com/us/podcast/the-mental-health-emergency-no-one-can-escape/id1804185340?i=1000727211753",
    topic: "Access to psychiatric care, telepsychiatry, and workforce gaps",
    datePublished: "2025-09-17",
  },
];

export const personSchema = {
  "@context": "https://schema.org",
  "@type": ["Person", "Physician"],
  "@id": PERSON_ID,
  name: "Shariq Refai",
  alternateName: "Shariq Refai, MD, MBA",
  honorificPrefix: "Dr.",
  honorificSuffix: "MD, MBA, FAPA",
  jobTitle: ["Psychiatrist", "Author", "Founder", "Mental Health Educator"],
  description:
    "Board-certified psychiatrist, author, mental health educator, founder of shrinkMD and shrinQ, and creator of Unstuck, based in Jacksonville, Florida.",
  url: SITE_ORIGIN,
  image: `${SITE_ORIGIN}/img/headshot.webp`,
  medicalSpecialty: ["https://schema.org/Psychiatric"],
  publishingPrinciples: EDITORIAL_PROCESS_URL,
  mainEntityOfPage: { "@type": "ProfilePage", "@id": `${SITE_ORIGIN}/#webpage`, url: `${SITE_ORIGIN}/` },
  worksFor: [
    { "@id": CLINICAL_ORG_URL },
    {
      "@type": "Organization",
      "@id": SHRINK_NETWORK_ORG_URL,
      name: "The Shrink Network",
      url: "https://shrinknetwork.com",
      sameAs: [SHRINK_NETWORK_WIKIDATA],
    },
  ],
  founderOf: [
    {
      "@type": "Organization",
      "@id": "https://shrinknetwork.com/#organization",
      name: "Shrink Network",
      url: "https://shrinknetwork.com",
      sameAs: [SHRINK_NETWORK_WIKIDATA],
      description:
        "An umbrella of independent editorial mental health publications founded and medically reviewed by Shariq Refai, MD, MBA.",
    },
    {
      "@type": "MedicalOrganization",
      "@id": CLINICAL_ORG_URL,
      name: "shrinkMD",
      legalName: "shrinkMD Health Inc.",
      url: "https://shrinkmd.com",
      sameAs: [SHRINKMD_WIKIDATA],
      description:
        "Independent multistate telepsychiatry practice operated by shrinkMD Health Inc.",
    },
    { "@type": "Organization", "@id": "https://shrinq.com/#organization", name: "shrinQ", url: "https://shrinq.com", sameAs: [SHRINQ_WIKIDATA] },
    { "@type": "Organization", "@id": "https://beunstuck.app/#organization", name: "Unstuck", url: "https://beunstuck.app" },
    {
      "@type": "Organization",
      "@id": PUBLISHER_ID,
      name: "shrinkMD Publishing",
      legalName: "shrinkMD Publishing, LLC",
      url: SITE_ORIGIN,
    },
  ],
  alumniOf: [
    { "@type": "CollegeOrUniversity", name: "University of North Florida", url: "https://www.unf.edu" },
    { "@type": "CollegeOrUniversity", name: "St. George's University School of Medicine", url: "https://www.sgu.edu" },
    { "@type": "CollegeOrUniversity", name: "University of Hawaii", url: "https://www.hawaii.edu" },
    { "@type": "Hospital", name: "John Peter Smith Hospital" },
    { "@type": "CollegeOrUniversity", name: "Duke University Fuqua School of Business", url: "https://www.fuqua.duke.edu" },
    { "@type": "CollegeOrUniversity", name: "Columbia University", url: "https://www.columbia.edu" },
  ],
  knowsAbout: [
    "psychiatry",
    "anxiety",
    "overthinking",
    "panic disorder",
    "depression",
    "obsessive-compulsive disorder",
    "post-traumatic stress disorder",
    "sports psychiatry",
    "performance psychiatry",
    "addiction psychiatry",
    "telepsychiatry",
    "digital mental health",
    "mental health education",
    "cognitive behavioral therapy",
    "psychiatric medication management",
  ],
  owns: [
    {
      "@type": "Brand",
      name: "shrinkMD",
      description:
        "Federally registered U.S. service mark for telepsychiatry and related mental health services.",
      logo: `${SITE_ORIGIN}/img/shrinkmd-logo.webp`,
      identifier: [
        {
          "@type": "PropertyValue",
          propertyID: "USPTO Registration Number",
          value: "7403744",
        },
        {
          "@type": "PropertyValue",
          propertyID: "USPTO Serial Number",
          value: "97488383",
        },
      ],
    },
    {
      "@type": "Brand",
      name: "shrinQ",
      description:
        "Federally registered U.S. service mark for online mental health information services.",
      logo: `${SITE_ORIGIN}/img/shrinq-logo-on-dark.png`,
      identifier: [
        {
          "@type": "PropertyValue",
          propertyID: "USPTO Registration Number",
          value: "8189949",
        },
        {
          "@type": "PropertyValue",
          propertyID: "USPTO Serial Number",
          value: "98953109",
        },
      ],
    },
  ],
  hasOccupation: [
  {
    "@type": "Occupation",
    name: "Physician",
    description: "Licensed medical doctor (MD) practicing in the specialty of psychiatry.",
  },
  {
    "@type": "Occupation",
    name: "Psychiatrist",
    occupationalCategory: "29-1223.00 Psychiatrists",
    occupationLocation: {
      "@type": "City",
      name: "Jacksonville",
      containedInPlace: {
        "@type": "State",
        name: "Florida",
        containedInPlace: { "@type": "Country", name: "United States" },
      },
    },
    skills: [
      "Adult psychiatric evaluation and diagnosis",
      "Psychiatric medication management",
      "Anxiety and panic disorder treatment",
      "Depression and mood disorder treatment",
      "OCD and PTSD treatment",
      "Sports and performance psychiatry",
      "Telepsychiatry",
    ],
    responsibilities: [
      "Independent multistate telepsychiatry practice through shrinkMD",
      "Medical review of educational mental health publications",
      "Author of forthcoming books on anxiety and overthinking",
      "Medical editor of The Shrink Network",
    ],
    qualifications: "Board certified in Psychiatry (ABPN) and in Sports and Performance Psychiatry. Fellow of the American Psychiatric Association (FAPA). MD from St. George's University School of Medicine, residency at University of Hawaii and John Peter Smith Hospital, MBA from Duke University.",
    experienceRequirements: "More than fifteen years of experience in psychiatry and mental health care.",
  },
  ],
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Board Certification",
      name: "Board-Certified Psychiatrist",
      recognizedBy: { "@type": "Organization", name: "American Board of Psychiatry and Neurology" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Board Certification",
      name: "Board-Certified in Sports and Performance Psychiatry",
      recognizedBy: { "@type": "Organization", name: "American Board of Sports and Performance Psychiatry" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Certificate",
      name: "Certificate in Electroconvulsive Therapy",
      recognizedBy: { "@type": "CollegeOrUniversity", name: "Columbia University" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Certificate",
      name: "Certificate in Obesity Medicine",
      recognizedBy: { "@type": "CollegeOrUniversity", name: "Columbia University" },
    },
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Fellowship",
      name: "Fellow of the American Psychiatric Association (FAPA)",
      recognizedBy: {
        "@type": "Organization",
        name: "American Psychiatric Association",
        url: "https://www.psychiatry.org",
      },
    },
  ],
  memberOf: [
    { "@id": SHRINK_NETWORK_ORG_URL },
    { "@type": "Organization", name: "Forbes Business Council", url: "https://councils.forbes.com" },
    { "@type": "Organization", name: "American Psychiatric Association", url: "https://www.psychiatry.org" },
    { "@type": "Organization", name: "Duval County Medical Society" },
  ],
  affiliation: [
    { "@type": "Organization", "@id": "https://shrinknetwork.com/#organization", name: "Shrink Network", url: "https://shrinknetwork.com" },
    { "@type": "Organization", "@id": "https://shrinkmd.com/#organization", name: "shrinkMD", url: "https://shrinkmd.com" },
    { "@type": "Organization", "@id": "https://shrinq.com/#organization", name: "shrinQ", url: "https://shrinq.com" },
    { "@type": "Organization", "@id": "https://anxietyresource.org/#organization", name: "Anxiety Resource", url: "https://anxietyresource.org" },
    { "@type": "Organization", "@id": "https://depressionresource.org/#organization", name: "Depression Resource", url: "https://depressionresource.org" },
    { "@type": "Organization", "@id": "https://anxietyresearch.org/#organization", name: "Anxiety Research", url: "https://anxietyresearch.org" },
    { "@type": "Organization", "@id": "https://psychiatryrx.org/#organization", name: "PsychiatryRx", url: "https://psychiatryrx.org" },
    { "@type": "Organization", "@id": `${SITE_ORIGIN}/#shrinkmd-publishing`, name: "shrinkMD Publishing", url: `${SITE_ORIGIN}/projects` },
  ],
  award: [
    "Top Doctor, Jacksonville Magazine",
    "Best in Florida Top Doctor",
    "FL Top Doc, Psychiatry (April 2024, FL Top Docs / USA Top Docs)",
    "FL Top Doc, Psychiatry (May 2025, FL Top Docs / USA Top Docs)",
    "FL Top Doc, Psychiatry (April 2026, FL Top Docs / USA Top Docs)",
  ],
  identifier: [
    {
      "@type": "PropertyValue",
      propertyID: "ORCID",
      value: "0009-0009-1090-4373",
      url: "https://orcid.org/0009-0009-1090-4373",
    },
    {
      "@type": "PropertyValue",
      propertyID: "Wikidata",
      value: "Q139822307",
      url: "https://www.wikidata.org/wiki/Q139822307",
    },
    {
      "@type": "PropertyValue",
      propertyID: "NPI",
      name: "National Provider Identifier",
      value: NPI,
      url: NPI_REGISTRY_URL,
    },
  ],
  reviewerOf: [
    {
      "@type": "WebSite",
      "@id": "https://shrinknetwork.com/#website",
      name: "Shrink Network",
      url: "https://shrinknetwork.com",
      description:
        "An umbrella of independent editorial mental health publications, with shared editorial and safety standards.",
    },
    {
      "@type": "WebSite",
      "@id": "https://anxietyresource.org/#website",
      name: "AnxietyResource.org",
      url: "https://anxietyresource.org",
      sameAs: ANXIETY_RESOURCE_REVIEWER_URL,
      description: "An independent, clinically reviewed editorial publication on anxiety.",
    },
    {
      "@type": "WebSite",
      "@id": "https://depressionresource.org/#website",
      name: "DepressionResource.org",
      url: "https://depressionresource.org",
      sameAs: DEPRESSION_RESOURCE_REVIEWER_URL,
      description: "A long-form, clinically reviewed editorial publication on depression.",
    },
    {
      "@type": "WebSite",
      "@id": "https://anxietyresearch.org/#website",
      name: "AnxietyResearch.org",
      url: "https://anxietyresearch.org",
      sameAs: ANXIETY_RESEARCH_REVIEWER_URL,
      description: "An independent, clinically reviewed editorial publication translating anxiety research for general readers.",
    },
    {
      "@type": "WebSite",
      "@id": "https://psychiatryrx.org/#website",
      name: "PsychiatryRx.org",
      url: "https://psychiatryrx.org",
      sameAs: PSYCHIATRY_RX_REVIEWER_URL,
      description: "An independent, clinically reviewed reference site publishing plain-language, psychiatrist-reviewed guides to psychiatric and sleep medications.",
    },
  ],
  subjectOf: [
    {
      "@type": "NewsArticle",
      headline: "10 Research-Backed, Natural Ways to Counter Depression",
      url: "https://www.theepochtimes.com/health/research-backed-natural-ways-to-treat-depression-5873396",
      datePublished: "2025-07-04",
      publisher: {
        "@type": "NewsMediaOrganization",
        name: "The Epoch Times",
        url: "https://www.theepochtimes.com",
      },
      about: [
        { "@type": "Thing", name: "Depression" },
        { "@type": "Thing", name: "Natural approaches to mental health" },
      ],
      mentions: { "@id": PERSON_ID },
    },
    {
      "@type": "NewsArticle",
      headline: "Do You Poop When You're Stressed? Experts Explain Why",
      url: "https://www.eatingwell.com/why-does-stress-make-you-poop-11776377",
      publisher: {
        "@type": "NewsMediaOrganization",
        name: "EatingWell",
        url: "https://www.eatingwell.com",
      },
      about: [
        { "@type": "Thing", name: "Stress" },
        { "@type": "Thing", name: "Gut brain axis" },
        { "@type": "Thing", name: "Nervous system" },
      ],
      mentions: { "@id": PERSON_ID },
    },
    {
      "@type": "NewsArticle",
      headline:
        "Survivors in Isolation: Abusers Separate Victims from Everyone for Power and Control",
      url: "https://www.domesticshelters.org/articles/identifying-abuse/survivors-in-isolation-abusers-separate-victims-from-everyone-for-power-and-control",
      datePublished: "2025-08-11",
      publisher: {
        "@type": "NewsMediaOrganization",
        name: "DomesticShelters.org",
        url: "https://www.domesticshelters.org",
      },
      about: [
        { "@type": "Thing", name: "Coercive control" },
        { "@type": "Thing", name: "Domestic abuse" },
        { "@type": "Thing", name: "Isolation and trauma" },
      ],
      mentions: { "@id": PERSON_ID },
    },
    {
      "@type": "Article",
      headline: "Mental Health Does Not Discriminate: An Interview With Dr. Shariq Refai",
      url: "https://topdoctormagazine.com/mental-health/mental-health-does-not-discriminate-an-interview-with-dr-shariq-refai/",
      publisher: { "@type": "Organization", name: "Top Doctor Magazine", url: "https://topdoctormagazine.com" },
      about: { "@id": PERSON_ID },
    },
    {
      "@type": "Article",
      headline: "Full Circle Calling: Spotlight on Shariq Refai, MD, MBA, FAPA",
      url: "https://www.dcmsonline.org/news/700524/Full-Circle-Calling-Spotlight-on-DCMS-Member-Shariq-Refai-MD-MBA-FAPA.htm",
      publisher: { "@type": "Organization", name: "Duval County Medical Society", url: "https://www.dcmsonline.org" },
      about: { "@id": PERSON_ID },
    },
    {
      "@type": "PodcastEpisode",
      name: "The Mental Health Emergency No One Can Escape",
      url: "https://podcasts.apple.com/us/podcast/the-mental-health-emergency-no-one-can-escape/id1804185340?i=1000727211753",
      datePublished: "2025-09-17",
      timeRequired: "PT6M",
      partOfSeries: {
        "@type": "PodcastSeries",
        name: "MedStory Podcast",
        url: "https://podcasts.apple.com/us/podcast/medstory-podcast/id1804185340",
      },
      publisher: {
        "@type": "Organization",
        name: "MedStory Studio",
        url: "https://medstorystudio.com",
      },
      about: [
        { "@type": "Thing", name: "Access to psychiatric care" },
        { "@type": "Thing", name: "Telepsychiatry" },
        { "@type": "Thing", name: "Mental health workforce" },
      ],
      actor: { "@id": PERSON_ID },
      mentions: { "@id": PERSON_ID },
    },
    {
      "@type": "CreativeWorkSeries",
      name: "Shrink Wrapped with Dr. Refai",
      alternateName: "Shrink Wrapped",
      description:
        "An ongoing video series unpacking psychiatry's most-asked questions with board-certified psychiatrist Shariq Refai, MD, MBA, produced by SoMeDocs (Doctors on Social Media).",
      url: "https://doctorsonsocialmedia.com/shrink-wrapped-with-dr-refai/",
      publisher: {
        "@type": "Organization",
        name: "SoMeDocs (Doctors on Social Media)",
        url: "https://doctorsonsocialmedia.com",
      },
      creator: { "@id": PERSON_ID },
      about: [
        { "@type": "Thing", name: "Psychiatry" },
        { "@type": "Thing", name: "Anxiety" },
        { "@type": "Thing", name: "Bipolar disorder" },
        { "@type": "Thing", name: "Athlete mental health" },
        { "@type": "Thing", name: "Women's mental health" },
      ],
    },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Jacksonville",
    addressRegion: "FL",
    addressCountry: "US",
  },
  contactPoint: {
    "@type": "ContactPoint",
    email: "media@shariqrefai.com",
    contactType: "Media and Press",
    availableLanguage: ["en"],
    areaServed: "Worldwide",
  },
  sameAs: [
    "https://shrinkmd.com/shariq-refai",
    "https://shrinkmd.com",
    "https://beunstuck.app/",
    "https://apps.apple.com/us/app/unstuck-app/id6766962969",
    "https://www.linkedin.com/in/shariqrefai",
    "https://councils.forbes.com/profile/Shariq-Refai-MD-MBA-Board-Certified-Psychiatrist-%7C-Founder-CEO-shrinkMD-shrinkMD/c1649312-6792-4cd3-b764-df4c393ec6c7",
    "https://www.dcmsonline.org/news/700524/Full-Circle-Calling-Spotlight-on-DCMS-Member-Shariq-Refai-MD-MBA-FAPA.htm",
    "https://topdoctormagazine.com/mental-health/mental-health-does-not-discriminate-an-interview-with-dr-shariq-refai/",
    "https://www.theepochtimes.com/health/research-backed-natural-ways-to-treat-depression-5873396",
    "https://www.eatingwell.com/why-does-stress-make-you-poop-11776377",
    "https://www.domesticshelters.org/articles/identifying-abuse/survivors-in-isolation-abusers-separate-victims-from-everyone-for-power-and-control",
    "https://www.doximity.com/pub/shariq-refai-md",
    "https://www.healthgrades.com/physician/dr-shariq-refai-xfq4b",
    "https://shrinknetwork.com",
    "https://shrinkdaily.com",
    "https://shrinkopedia.com",
    "https://shrinktionary.com",
    "https://shrinq.com",
    "https://quitscrolling.app",
    "https://shrinkiatry.com",
    "https://orcid.org/0009-0009-1090-4373",
    "https://www.wikidata.org/wiki/Q139822307",
    NPI_REGISTRY_URL,
    "https://anxietyresource.org/contributors/shariq-refai/",
    "https://depressionresource.org/about/shariq-refai/",
    "https://anxietyresearch.org/medical-review-board/",
    "https://psychiatryrx.org/medical-editor/",
    "https://medstorystudio.com/shariqrefai",
    "https://www.psychiatrist.com/author/shariq-refai/",
    "https://doctorsonsocialmedia.com/shariq-refai-md-mba/",
    "https://doctorsonsocialmedia.com/shrink-wrapped-with-dr-refai/",
    "https://podcasts.apple.com/us/podcast/the-mental-health-emergency-no-one-can-escape/id1804185340?i=1000727211753",
    "https://fltopdocs.com/april-2024/",
    "https://fltopdocs.com/fl-top-docs-proudly-presents-may-2025-approved-providers/",
    "https://fltopdocs.com/april-2026/",
  ],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": ORG_ID,
  url: SITE_ORIGIN,
  name: SITE_NAME,
  description:
    "Educational author and media platform for Shariq Refai, MD, MBA, board-certified psychiatrist, founder of shrinkMD, author, and educator.",
  inLanguage: "en-US",
  publisher: { "@id": PERSON_ID },
  copyrightHolder: { "@id": PERSON_ID },
  // All rights reserved, with AI crawling, retrieval, and training on the
  // public pages allowed with attribution. Terms: /copyright/ and /ai.txt.
  license: `${SITE_ORIGIN}/copyright/`,
};

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export function breadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: abs(it.path),
    })),
  };
}

export interface WebPageInput {
  path: string;
  title: string;
  description: string;
  type?: "WebPage" | "AboutPage" | "ContactPage" | "CollectionPage" | "MedicalWebPage" | "ProfilePage";
  breadcrumbs?: BreadcrumbItem[];
  image?: string;
  speakableSelectors?: string[];
  datePublished?: string;
  dateModified?: string;
  lastReviewed?: string;
  reviewedByPerson?: boolean;
  author?: boolean;
}

export const SITE_LAST_REVIEWED = "2026-05-15";

export function webPageSchema({
  path,
  title,
  description,
  type = "WebPage",
  breadcrumbs,
  image,
  speakableSelectors,
  datePublished,
  dateModified,
  lastReviewed = SITE_LAST_REVIEWED,
  reviewedByPerson = true,
  author = true,
}: WebPageInput) {
  const node: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": type,
    "@id": `${abs(path)}#webpage`,
    url: abs(path),
    name: title,
    description,
    inLanguage: "en-US",
    isPartOf: { "@id": ORG_ID },
    about: { "@id": PERSON_ID },
    primaryImageOfPage: { "@type": "ImageObject", url: image ? abs(image) : DEFAULT_IMAGE, width: 1200, height: 630 },
  };
  if (type === "ProfilePage") node.mainEntity = { "@id": PERSON_ID };
  if (author) node.author = { "@id": PERSON_ID };
  node.publisher = { "@id": PERSON_ID };
  node.publishingPrinciples = EDITORIAL_PROCESS_URL;
  if (lastReviewed) node.lastReviewed = lastReviewed;
  if (reviewedByPerson) node.reviewedBy = { "@id": PERSON_ID };
  if (breadcrumbs && breadcrumbs.length > 0) {
    node.breadcrumb = breadcrumbSchema(breadcrumbs);
  }
  if (speakableSelectors && speakableSelectors.length > 0) {
    node.speakable = { "@type": "SpeakableSpecification", cssSelector: speakableSelectors };
  }
  if (datePublished) node.datePublished = datePublished;
  if (dateModified) node.dateModified = dateModified;
  return node;
}

export interface FaqEntry {
  question: string;
  answer: string;
}

export function faqSchema(entries: FaqEntry[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entries.map((e) => ({
      "@type": "Question",
      name: e.question,
      acceptedAnswer: { "@type": "Answer", text: e.answer },
    })),
  };
}

export interface BookInput {
  path: string;
  name: string;
  description: string;
  bookFormat?: "Hardcover" | "Paperback" | "EBook" | "AudiobookFormat";
  inLanguage?: string;
  workExample?: { name: string; bookFormat?: string }[];
  image?: string;
  abstract?: string;
  headline?: string;
  datePublished?: string;
  publisher?: string;
  genre?: string[];
  alternateName?: string;
  isPartOfBookId?: string;
}

export function bookSchema({
  path,
  name,
  description,
  bookFormat = "Paperback",
  inLanguage = "en",
  image,
  abstract,
  headline,
  datePublished,
  publisher,
  genre,
  alternateName,
  isPartOfBookId,
}: BookInput) {
  const node: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Book",
    "@id": `${abs(path)}#book`,
    name,
    description,
    abstract: abstract ?? description,
    author: { "@id": PERSON_ID },
    inLanguage,
    bookFormat: `https://schema.org/${bookFormat}`,
    image: image ? abs(image) : DEFAULT_IMAGE,
    url: abs(path),
    publisher: publisher
      ? { "@type": "Organization", name: publisher }
      : { "@id": PUBLISHER_ID },
  };
  if (headline) node.headline = headline;
  if (datePublished) node.datePublished = datePublished;
  if (genre) node.genre = genre;
  if (alternateName) node.alternateName = alternateName;
  if (isPartOfBookId) node.isPartOf = { "@id": isPartOfBookId };
  return node;
}

export interface CollectionItem {
  name: string;
  path: string;
  description?: string;
}

export function collectionPageSchema({
  path,
  title,
  description,
  items,
  breadcrumbs,
  speakableSelectors,
}: {
  path: string;
  title: string;
  description: string;
  items: CollectionItem[];
  breadcrumbs?: BreadcrumbItem[];
  speakableSelectors?: string[];
}) {
  const base = webPageSchema({ path, title, description, type: "CollectionPage", breadcrumbs, speakableSelectors });
  return {
    ...base,
    mainEntity: {
      "@type": "ItemList",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: items.length,
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: abs(it.path),
        name: it.name,
      })),
    },
  };
}

export interface BlogPostingInput {
  slug: string;
  title: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
  citations?: CitationInput[];
  wordCount?: number;
}

export interface CitationInput {
  name: string;
  author?: string;
  datePublished?: string;
  url?: string;
  journal?: string;
}

export function citationsToSchema(citations: CitationInput[]) {
  return citations.map((c) => {
    const node: Record<string, any> = {
      "@type": "ScholarlyArticle",
      name: c.name,
    };
    if (c.author) node.author = c.author;
    if (c.datePublished) node.datePublished = c.datePublished;
    if (c.url) node.url = c.url;
    if (c.journal) node.isPartOf = { "@type": "Periodical", name: c.journal };
    return node;
  });
}

export function blogPostingSchema({
  slug,
  title,
  description,
  datePublished,
  dateModified,
  image,
  citations,
  wordCount,
}: BlogPostingInput) {
  const url = `${SITE_ORIGIN}/perspectives/${slug}`;
  const node: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": url,
    headline: title,
    description,
    author: { "@id": PERSON_ID },
    publisher: { "@id": PERSON_ID },
    publishingPrinciples: EDITORIAL_PROCESS_URL,
    datePublished,
    dateModified: dateModified ?? datePublished,
    image: image ?? `${SITE_ORIGIN}/og-perspectives.jpg`,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: "en-US",
    isPartOf: { "@id": `${SITE_ORIGIN}/perspectives#blog` },
    speakable: { "@type": "SpeakableSpecification", cssSelector: [".quick-answer", ".faq"] },
  };
  if (citations && citations.length > 0) {
    node.citation = citationsToSchema(citations);
  }
  if (typeof wordCount === "number" && wordCount > 0) {
    node.wordCount = wordCount;
  }
  return node;
}

export function blogSchema(posts: { slug: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${SITE_ORIGIN}/perspectives#blog`,
    url: `${SITE_ORIGIN}/perspectives`,
    name: "Perspectives by Shariq Refai, MD, MBA",
    description:
      "Educational essays from Shariq Refai, MD, MBA on psychiatry, anxiety, overthinking, performance, digital mental health, and emotional health.",
    author: { "@id": PERSON_ID },
    publisher: { "@id": PERSON_ID },
    inLanguage: "en-US",
    blogPost: posts.map((p) => ({ "@id": `${SITE_ORIGIN}/perspectives/${p.slug}` })),
  };
}

export const medicalOrganizationSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalOrganization",
  "@id": CLINICAL_ORG_URL,
  name: "shrinkMD",
  legalName: "shrinkMD Health Inc.",
  url: "https://shrinkmd.com",
  founder: { "@id": PERSON_ID },
  description:
    "Independent multistate telepsychiatry practice operated by shrinkMD Health Inc., providing virtual psychiatric evaluation and medication management for adults.",
  brand: {
    "@type": "Brand",
    name: "shrinkMD",
    identifier: {
      "@type": "PropertyValue",
      propertyID: "USPTO Registration Number",
      value: "7403744",
    },
  },
  sameAs: ["https://shrinkmd.com/shariq-refai"],
};

// shrinkMD Publishing, LLC — the operating publisher for shariqrefai.com, the
// Shrink Network educational sites, and the forthcoming books. Distinct from
// the clinical entity shrinkMD Health Inc. Founded and solely owned by Shariq
// Refai, MD, MBA.
export const publisherOrgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": PUBLISHER_ID,
  name: "shrinkMD Publishing",
  legalName: "shrinkMD Publishing, LLC",
  url: SITE_ORIGIN,
  founder: { "@id": PERSON_ID },
  logo: {
    "@type": "ImageObject",
    url: `${SITE_ORIGIN}/favicon-512.png`,
    width: 512,
    height: 512,
  },
  knowsAbout: ["psychiatry", "anxiety", "overthinking", "mental health education", "digital mental health"],
  memberOf: { "@type": "Organization", "@id": SHRINK_NETWORK_ORG_URL, name: "The Shrink Network", sameAs: SHRINK_NETWORK_WIKIDATA },
  sameAs: [SHRINK_NETWORK_URL],
  contactPoint: [
    { "@type": "ContactPoint", contactType: "Editorial", email: "editor@shariqrefai.com", availableLanguage: ["en"] },
    { "@type": "ContactPoint", contactType: "Media and Press", email: "media@shariqrefai.com", availableLanguage: ["en"] },
    { "@type": "ContactPoint", contactType: "Corrections", email: "corrections@shariqrefai.com", availableLanguage: ["en"] },
  ],
  ethicsPolicy: `${SITE_ORIGIN}/disclosures/`,
  publishingPrinciples: `${SITE_ORIGIN}/editorial-process/`,
  correctionsPolicy: `${SITE_ORIGIN}/corrections/`,
  actionableFeedbackPolicy: `${SITE_ORIGIN}/contact/`,
  masthead: `${SITE_ORIGIN}/medical-review-board/`,
  verificationFactCheckingPolicy: `${SITE_ORIGIN}/evidence/`,
  address: {
    "@type": "PostalAddress",
    streetAddress: "7901 4th St N STE 300",
    addressLocality: "St. Petersburg",
    addressRegion: "FL",
    postalCode: "33702",
    addressCountry: "US",
  },
  identifier: [
    {
      "@type": "PropertyValue",
      propertyID: "EIN",
      name: "Employer Identification Number",
      value: "42-2585573",
    },
  ],
  description:
    "Independent U.S. publisher of shariqrefai.com, the Shrink Network educational sites, and the forthcoming books by Shariq Refai, MD, MBA.",
};

export const mobileApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  "@id": "https://beunstuck.app/#app",
  name: "Unstuck",
  operatingSystem: "iOS",
  applicationCategory: "HealthApplication",
  url: "https://beunstuck.app",
  downloadUrl: "https://apps.apple.com/us/app/unstuck-app/id6766962969",
  creator: { "@id": PERSON_ID },
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

// Peer-reviewed publication authored by Dr. Refai. Emitted on /press (and any
// page where his authored scholarly work is contextually relevant). Author @id
// links back to the canonical Person node.
export const priapismCaseScholarlyArticle = {
  "@context": "https://schema.org",
  "@type": "ScholarlyArticle",
  "@id": "https://www.psychiatrist.com/pcc/case-priapism-associated-rapid-increase-risperidone/#article",
  headline: "A Case of Priapism Associated With Rapid Increase in Risperidone Dose",
  name: "A Case of Priapism Associated With Rapid Increase in Risperidone Dose",
  url: "https://www.psychiatrist.com/pcc/case-priapism-associated-rapid-increase-risperidone/",
  datePublished: "2012-09-20",
  inLanguage: "en",
  author: [
    { "@id": PERSON_ID },
    { "@type": "Person", name: "Helenna H. Nakama, MD" },
  ],
  publisher: {
    "@type": "Organization",
    name: "Physicians Postgraduate Press, Inc.",
    url: "https://www.psychiatrist.com",
  },
  isPartOf: {
    "@type": "Periodical",
    name: "Primary Care Companion for CNS Disorders",
    alternateName: "Prim Care Companion CNS Disord",
    issn: ["2155-7780", "2155-7772"],
  },
  identifier: [
    {
      "@type": "PropertyValue",
      propertyID: "DOI",
      value: "10.4088/PCC.12l01365",
    },
  ],
  about: [
    { "@type": "Thing", name: "Risperidone" },
    { "@type": "Thing", name: "Priapism" },
    { "@type": "Thing", name: "Antipsychotic side effects" },
  ],
};


export interface ServiceInput {
  name: string;
  description: string;
  serviceType?: string;
  areaServed?: string;
}

export function serviceSchema({
  name,
  description,
  serviceType = "Keynote Speaking",
  areaServed = "Worldwide",
}: ServiceInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    provider: { "@id": PERSON_ID },
    serviceType,
    description,
    areaServed,
  };
}
